import { QuantitySelectorComponent } from '@theme/component-quantity-selector';

/**
 * A custom element that allows the user to select a quantity in the cart.
 * Extends QuantitySelectorComponent but uses absolute max limits instead of effective max.
 * Semantics: "What should the total quantity BE in the cart" vs "How many to ADD to cart"
 *
 * @extends {QuantitySelectorComponent}
 */
class CartQuantitySelectorComponent extends QuantitySelectorComponent {
  /**
   * Gets the effective maximum value for cart quantity selector
   * Cart page: uses absolute max (how much can be in cart total)
   * @returns {number | null} The effective max, or null if no max
   */
  getEffectiveMax() {
    const { max } = this.getCurrentValues();
    return max; // Cart uses absolute max, not max minus cart quantity
  }

  /**
   * SOM: in the cart a line can go down to 0, so pressing "−" at quantity 1 removes the
   * product (the cart treats quantity 0 as "remove line", same as the trash icon).
   * @returns {{min: number, max: number|null, step: number, value: number, cartQuantity: number}}
   */
  getCurrentValues() {
    return { ...super.getCurrentValues(), min: 0 };
  }

  /**
   * SOM: if the quantity field is emptied and left, restore the previous quantity
   * instead of removing the product by accident. Typing 0 still removes it.
   * @param {Event} event - The blur event.
   */
  setQuantity(event) {
    if (event.target instanceof HTMLInputElement && event.target.value.trim() === '') {
      event.target.value = event.target.defaultValue;
      return;
    }

    super.setQuantity(event);
  }

  /**
   * Updates button states based on current value and limits
   * Cart buttons are always managed client-side, never server-disabled
   */
  updateButtonStates() {
    const { minusButton, plusButton, quantityInput } = this.refs;
    const { min, value } = this.getCurrentValues();
    const effectiveMax = this.getEffectiveMax();

    // SOM: keep the input's own min in sync (also after the cart re-renders), so a 0 is never flagged invalid
    quantityInput.min = String(min);

    // Cart buttons are always dynamically managed
    minusButton.disabled = value <= min;
    plusButton.disabled = effectiveMax !== null && value >= effectiveMax;
  }
}

if (!customElements.get('cart-quantity-selector-component')) {
  customElements.define('cart-quantity-selector-component', CartQuantitySelectorComponent);
}
